/**
 * ForceChangePasswordPage.jsx — Mandatory Password Change
 *
 * Shown immediately after login when must_change_password = true.
 * The user cannot navigate away — all protected routes redirect here
 * until the password is changed.
 *
 * Uses the existing /users/me/password endpoint (requires current password).
 * The admin sets the temp password via Force Reset; the user enters it here
 * as the "current password" before setting their own new one.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, AlertCircle, ShieldCheck, Zap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { changePassword } from '../utils/api';
import logger from '../utils/logger';
import './LoginPage.css';

export default function ForceChangePasswordPage() {
  const { user, token, updateUser, upgradeSession, logout } = useAuth();
  const navigate = useNavigate();

  const [form, setForm]         = useState({ current: '', newPwd: '', confirm: '' });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew]         = useState(false);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (form.newPwd.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }
    if (form.newPwd !== form.confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (form.newPwd === form.current) {
      setError('New password must be different from the temporary password.');
      return;
    }

    setLoading(true);
    try {
      const result = await changePassword(token, form.current, form.newPwd);
      // Clear the flag in local state — user is now unblocked
      updateUser({ must_change_password: false });
      // Backend returns full tokens to replace the 5-min forced-reset token
      if (result?.token && result?.refresh_token) {
        upgradeSession(result.token, result.refresh_token);
      }
      logger.info('ForceChangePassword: Password changed, navigating to chat');
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-bg-grid" aria-hidden="true" />
      <div className="login-orb login-orb--1" aria-hidden="true" />
      <div className="login-orb login-orb--2" aria-hidden="true" />

      <div className="login-container">

        {/* Brand */}
        <div className="login-brand">
          <div className="login-logo">
            <Zap size={28} strokeWidth={2.5} />
          </div>
          <div className="login-brand-text">
            <span className="login-brand-name">Querely</span>
            <span className="login-brand-tagline">Your SAP, in plain English</span>
          </div>
        </div>

        {/* Card */}
        <div className="login-card">
          <div className="login-card-header">
            <div className="reset-step-icon reset-step-icon--warning">
              <ShieldCheck size={22} />
            </div>
            <h1 className="login-title">Set a new password</h1>
            <p className="login-subtitle">
              Hi <strong style={{ color: 'var(--clr-text-primary)' }}>{user?.name}</strong>,
              your administrator has reset your password.
              You must set a new password before continuing.
            </p>
          </div>

          {error && (
            <div className="login-error" role="alert">
              <AlertCircle size={16} /><span>{error}</span>
            </div>
          )}

          <form className="login-form" onSubmit={handleSubmit} noValidate>

            {/* Temporary password */}
            <div className="login-field">
              <label className="login-label" htmlFor="cur-pwd">
                Temporary password (given by admin)
              </label>
              <div className="login-password-wrapper">
                <input
                  id="cur-pwd"
                  type={showCurrent ? 'text' : 'password'}
                  className="login-input login-input--password"
                  value={form.current}
                  onChange={e => { setForm(f => ({ ...f, current: e.target.value })); setError(''); }}
                  placeholder="Enter temporary password"
                  autoFocus
                  disabled={loading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowCurrent(v => !v)}
                  disabled={loading}
                  aria-label={showCurrent ? 'Hide' : 'Show'}
                >
                  {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* New password */}
            <div className="login-field">
              <label className="login-label" htmlFor="new-pwd">New password</label>
              <div className="login-password-wrapper">
                <input
                  id="new-pwd"
                  type={showNew ? 'text' : 'password'}
                  className="login-input login-input--password"
                  value={form.newPwd}
                  onChange={e => { setForm(f => ({ ...f, newPwd: e.target.value })); setError(''); }}
                  placeholder="Min 6 characters"
                  disabled={loading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowNew(v => !v)}
                  disabled={loading}
                  aria-label={showNew ? 'Hide' : 'Show'}
                >
                  {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Confirm */}
            <div className="login-field">
              <label className="login-label" htmlFor="confirm-pwd">Confirm new password</label>
              <input
                id="confirm-pwd"
                type="password"
                className="login-input"
                value={form.confirm}
                onChange={e => { setForm(f => ({ ...f, confirm: e.target.value })); setError(''); }}
                placeholder="Repeat new password"
                disabled={loading}
                autoComplete="new-password"
              />
            </div>

            <button type="submit" className="login-btn-primary" disabled={loading}>
              {loading
                ? <><Loader2 size={18} className="login-spinner" /> Saving…</>
                : 'Set new password & continue'}
            </button>
          </form>

          {/* Escape hatch — sign out if they don't know the temp password */}
          <button
            className="reset-back-link"
            onClick={logout}
            disabled={loading}
            style={{ marginTop: 'var(--sp-4)' }}
          >
            Sign out
          </button>
        </div>

        <p className="login-footer">
          Querely v1.0 &nbsp;·&nbsp; Powered by SAP OData + Azure OpenAI
        </p>
      </div>
    </div>
  );
}
